<template>
  <iProgressBar
    v-if="trackingEnabled"
    :value="percent"
    :width="myWidth"
    :height="myHeight"
    :border-radius="myBorderRadius"
    :fill-color="fillColor"
    :track-color="trackColor"
  />
</template>

<script>
import { StoryPageElementMixin } from "%/dynamic-component-editor/standard-pages/story-page/story-page-element-mixin";
import { useCompletionStatusStore } from "%/dynamic-component-editor/stores/completion-status-store";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import CompletionProgressFill from "%/dynamic-component-editor/options/completion-progress-fill";
import CompletionProgressMode from "%/dynamic-component-editor/options/completion-progress-mode";
import { ELEMENT_PANEL_FOLDER } from "%/dynamic-component-editor/constants/element-panel-folder-names";
import { resolveCompletionTrackColor } from "%/dynamic-component-editor/standard-components/base-story-card/completion-progress-track-color";
import { completionPercentForMode } from "%/dynamic-component-editor/standard-components/base-story-card/completion-progress-percent";

export default {
  name: "CompletionProgress",
  elementPanelSettings: {
    folder: ELEMENT_PANEL_FOLDER.COMPLETION,
    displayName: "Completion Progress",
    displayIcon: "mdi:progress-check",
    elementGroup: "storyElements",
    elementType: "element",
  },
  mixins: [StoryPageElementMixin],
  optionBundles: [
    new AppearanceOptions({}, { "border-radius": false }),
    new LayoutWidthHeightOptions({}, {
      "max-width": true,
      "max-height": true,
      "min-width": true,
      "min-height": true,
    }),
    new BackgroundOptions({}, {
      "background-gradient": true,
      "background-image": true,
    }),
  ],
  options: [
    CompletionProgressMode,
    CompletionProgressFill,
  ],
  data() {
    return {
      CompletionProgressFill,
      CompletionProgressMode,
      BackgroundColor,
    };
  },
  computed: {
    completionPublicationId() {
      return useConfigurationStore().publicationId;
    },
    trackingEnabled() {
      if (this.editMode) {
        return true;
      }
      return useCompletionAssignmentStore().isTrackingConfigured(this.completionPublicationId);
    },
    myWidth() {
      const value = this.getOptionValue(LayoutWidth);
      if (value === "hug") {
        return "100%";
      }
      return LayoutWidth.getCssValue(value);
    },
    myHeight() {
      const value = this.getOptionValue(LayoutHeight);
      if (value === "hug" || value === "fill") {
        return "8px";
      }
      return LayoutHeight.getCssValue(value);
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius) || "0px";
    },
    folderProgress() {
      if (this.editMode) {
        return { total: 5, completed: 2, percent: 40, childNodes: 5, completedChildNodes: 2, leafNodesInTree: 10, completedLeafNodesInTree: 4 };
      }
      const store = useCompletionStatusStore();
      return { percent: store.progressByFolder[this.storyEntityId]?.percent ?? null, ...store.folderCountsFor(this.storyEntityId, this.entity) };
    },
    percent() {
      return completionPercentForMode(this.getOptionValue(CompletionProgressMode), this.folderProgress);
    },
    fillColor() {
      return this.resolveColor(CompletionProgressFill);
    },
    trackColor() {
      return resolveCompletionTrackColor(this.resolveColor(BackgroundColor), this.fillColor);
    },
  },
  watch: {
    storyEntityId: {
      immediate: true,
      handler(storyId) {
        if (!this.editMode && storyId) {
          useCompletionStatusStore().ensureFolderProgress(storyId);
        }
      },
    },
    completionPublicationId: {
      immediate: true,
      handler(publicationId) {
        if (!this.editMode && publicationId) {
          useCompletionAssignmentStore().ensureAssignments(publicationId);
        }
      },
    },
  },
  methods: {
    resolveColor(optionClass) {
      const value = this.getOptionValue(optionClass);
      if (!value || typeof value !== "object") {
        return value;
      }
      return optionClass.convertConfigValueToCompiledValue(value, this.currentDcThemeId, this.themeMode);
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
